export default class StringUtils {
    public isNull = (obj) => {
        return obj === null || obj === undefined || obj === "";
    }

    public isNotNull = (obj) => {
        return obj !== null && obj !== undefined && obj !== "";
    }

    public firstToUpperCase = (str: string): string => {
        return  str[0].toUpperCase() + str.substring(1);
    }

    public capitalizeFirstLetter = (input: string) => {
        return input.charAt(0).toUpperCase() + input.slice(1);
    }

    public toCapital = (str: string): string => {
        return str[0].toUpperCase() + str.slice(1);
    }

   public toTitleCase = (str?: string): string => {
    if (!str) return ""; // return empty string if null/undefined

    return str
        .split(" ")
        .map(word => this.toCapital(word))
        .join(" ");
};


    public textToSlug = (str: string): string => {
        return  str.replace(/[\s.]/g, "-");
    }

    public slugToText = (str: string): string => {
        return str.replace(/[-]/g, " ");
    }

    public removeUnderscore = (str: string): string => {
        return str.replace(/[_]/g, " ");
    }

    public capitalizeString = (str: string): string => {
        if (str === "") {
            return str
        }

        const firstLetter = str.slice(0, 1)
        return `${firstLetter.toUpperCase()}${str.slice(1, str.length)}`
    }

    public generateUID = (str: string): string => {
        return (
            Math.random().toString(36).substr(2, 9) +
            Math.random().toString(36).substr(2, 9)
        );
    }

    public isEmpty = (input: string = '', removeSpaces: boolean = true): boolean => {
        return (
            removeSpaces
                ? input.replace(/\s/g, '').length === 0
                : input.length === 0
        );
    }

    public normalizeString = (str: string) => {
        return str.toLowerCase().trim()
    }

    public getCostCenterCode = (item: any): string => {
        const code = item?.organogram_code ?? item?.cost_center_code ?? item?.code ?? "";
        return String(code || "").trim();
    }

    public buildCostCenterLabel = (name: string, item?: any): string => {
        const code = this.getCostCenterCode(item);
        return name && code ? `${name} (${code})` : name || "";
    }

    public extractRemarksText = (value: any): string => {
        if (value === null || value === undefined) return "";
        if (typeof value === "string") return value.trim();
        if (typeof value === "number" || typeof value === "boolean") return String(value);

        const parts: string[] = [];
        const collect = (input: any): void => {
            if (input === null || input === undefined) return;
            if (typeof input === "string") {
                const text = input.trim();
                if (text) parts.push(text);
            } else if (typeof input === "number" || typeof input === "boolean") {
                parts.push(String(input));
            } else if (Array.isArray(input)) {
                input.forEach(collect);
            } else if (typeof input === "object") {
                Object.values(input).forEach(collect);
            }
        };
        collect(value);
        return parts.join(", ");
    }

    public getRemarkEntries = (value: any): any[] => {
        if (value === null || value === undefined) return [];
        let parsed = value;
        if (typeof parsed === "string") {
            const trimmed = parsed.trim();
            if (!trimmed) return [];
            try {
                parsed = JSON.parse(trimmed);
            } catch (e) {
                return [trimmed];
            }
        }
        if (Array.isArray(parsed)) return parsed;
        if (typeof parsed === "object") {
            const arrayKey = ["remarks", "history", "data", "rows", "status_history"].find((key) => Array.isArray(parsed[key]));
            if (arrayKey) return parsed[arrayKey];
            return [parsed];
        }
        return [parsed];
    }

    public extractRemarkText = (entry: any): string => {
        if (entry === null || entry === undefined) return "";
        if (typeof entry === "string") return entry.trim();
        if (typeof entry === "number" || typeof entry === "boolean") return String(entry);
        if (typeof entry === "object") {
            const textKeys = ["remarks", "remark", "text", "comment", "message", "note", "value", "reason"];
            for (const key of textKeys) {
                if (entry[key] !== undefined && entry[key] !== null) {
                    const text = String(entry[key]).trim();
                    if (text) return text;
                }
            }
            return this.extractRemarksText(entry);
        }
        return String(entry);
    }

    public extractLatestRemark = (value: any): string => {
        const entries = this.getRemarkEntries(value);
        if (!entries.length) return "";
        return this.extractRemarkText(entries[entries.length - 1]);
    }

    public collectAllRemarks = (itemData: any): any[] => {
        const allEntries: any[] = [];

        const addEntries = (value: any) => {
            if (value === null || value === undefined) return;
            const entries = this.getRemarkEntries(value);
            entries.forEach((entry) => {
                if (entry) {
                    allEntries.push(entry);
                }
            });
        };

        // Budget level remarks
        addEntries(itemData?.budget_remarks);
        addEntries(itemData?.remarks);

        // Status history remarks (approver remarks)
        const statusData = itemData?.status_data;
        if (statusData) {
            let parsed = statusData;
            if (typeof statusData === "string") {
                try {
                    parsed = JSON.parse(statusData);
                } catch (e) {
                    // ignore
                }
            }
            if (Array.isArray(parsed)) {
                parsed.forEach((s: any) => {
                    if (s?.remarks) {
                        allEntries.push({
                            type: "status",
                            from: s.from,
                            to: s.to,
                            remarks: s.remarks,
                            timestamp: s.timestamp,
                            name: s.name,
                            name_bn: s.name_bn,
                        });
                    }
                });
            }
        }

        // Detail level remarks
        if (Array.isArray(itemData?.budget_details)) {
            itemData.budget_details.forEach((detail: any) => {
                addEntries(detail?.budget_remarks);
                addEntries(detail?.remarks);
            });
        }

        return allEntries;
    }

    public extractAllRemarksText = (itemData: any): string => {
        const entries = this.collectAllRemarks(itemData);
        if (!entries.length) return "";
        const texts = entries
            .map((entry) => this.extractRemarkText(entry))
            .filter((text) => text && text.trim());
        if (!texts.length) return "";
        return texts.join("; ");
    }

    public randomString = (length: number) => {
        let charset =
            '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~';
        let result = '';

        while (length > 0) {
            let bytes = new Uint8Array(16);
            let random = window.crypto.getRandomValues(bytes);

            /* eslint-disable-next-line no-loop-func */
            random.forEach(c => {
                if (length === 0) {
                    return;
                }
                if (c < charset.length) {
                    result += charset[c];
                    length--;
                }
            });
        }
        return result;
    }


    public htmlToString = (html: string) => {
        return html?.replace(/(<([^>]+)>)/gi, "");
    }

    public jsonString = (value: any) => {
        return JSON.stringify(Array.from(value.entries()));
    }

    public truncateString = (string: string, length: number, ending: string) => {
        if (length === null) {
            length = 100;
        }
        if (ending === null) {
            ending = ' ...';
        }
        if (string?.length > length) {
            return string.substring(0, length - ending.length) + ending;
        } else {
            return string;
        }
    };

    public stripTags = (input: string, allowed: string) => {
        allowed = (((allowed || '') + '').toLowerCase().match(/<[a-z][a-z0-9]*>/g) || []).join('');
        const tags = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;
        return input.replace(tags, ($0, $1) => (allowed.indexOf('<' + $1.toLowerCase() + '>') > -1 ? $0 : ''));
    };

    public getUid = () => {
        return Math.random().toString(16).slice(2) + new Date().getTime();
    }

    public getDeviceUUID = () => {
        let deviceUuid = localStorage.getItem("deviceUuid");
        if (!deviceUuid) {
            deviceUuid = this.getUid();
            localStorage.setItem('deviceUuid', deviceUuid);
        }
        return deviceUuid;
    }

    public getVisitorId = () => {
        let visitorUuid = localStorage.getItem("visitorUuid");
        if (!visitorUuid) {
            visitorUuid = this.getUid();
            localStorage.setItem('visitorUuid', visitorUuid);
        }
        return visitorUuid;
    }

    public getCurrentBrowserUrl = () => {
        if (typeof window !== 'undefined') {
            return window.location.origin + window.location.pathname + window.location.search + window.location.hash;
        }
        return 'undefined';
    }

    public normalizeStatusValue = (value: any) => {
        if (value === true || value === 1 || value === "1") {
            return "1";
        }
        if (value === false || value === 0 || value === "0") {
            return "0";
        }
        return value;
};
}
